import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("La password deve avere almeno 6 caratteri.");
    if (password !== confirm) return setErr("Le due password non coincidono.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password aggiornata! Ora puoi accedere.");
      nav("/login");
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative bg-card border-r border-border overflow-hidden">
        <img
          src="https://images.pexels.com/photos/14314638/pexels-photo-14314638.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Freed Words"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="relative z-10 mt-auto p-12">
          <h2 className="font-heading text-4xl font-light tracking-tighter max-w-md">
            Scegli una nuova <span className="text-brand-pink">password</span>.
          </h2>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-8">
            <img src="/logo.png" alt="Freed Words" className="h-8 w-auto" />
            <span className="font-heading text-lg font-semibold">Freed<span className="text-brand-pink">Words</span></span>
          </Link>

          {!token ? (
            <div data-testid="reset-no-token">
              <h1 className="font-heading text-3xl font-light tracking-tight mb-2">Link non valido</h1>
              <p className="text-muted-foreground text-sm mb-8">Il link di reset è mancante o non valido. Richiedine uno nuovo.</p>
              <Link to="/forgot-password" data-testid="request-new"
                className="inline-flex items-center gap-2 text-sm text-brand-blue hover:underline">
                <ArrowLeft className="h-4 w-4" /> Richiedi un nuovo link
              </Link>
            </div>
          ) : (
            <>
              <div className="h-12 w-12 rounded-2xl bg-brand-pink/10 flex items-center justify-center mb-5">
                <ShieldCheck className="h-6 w-6 text-brand-pink" />
              </div>
              <h1 className="font-heading text-3xl font-light tracking-tight mb-1">Nuova password</h1>
              <p className="text-muted-foreground text-sm mb-8">Inserisci la nuova password per il tuo account.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Nuova password</Label>
                  <PasswordInput id="password" data-testid="reset-password" required value={password}
                    onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm">Conferma password</Label>
                  <PasswordInput id="confirm" data-testid="reset-confirm" required value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
                </div>
                {err && <p data-testid="reset-error" className="text-sm text-destructive">{err}</p>}
                <Button type="submit" data-testid="reset-submit" disabled={loading}
                  className="w-full rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white h-11">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reimposta password"}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
